import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { WORD_API_URL, IS_LOCAL_DEV } from '../config';
import AdminShell from './AdminShell';
import LetterGrid from './LetterGrid';
import ScheduleSettings from './ScheduleSettings';
import './AdminPanel.css';

function AdminPanel() {
  const [tomorrowWord, setTomorrowWord] = useState(null);
  const [todayWord, setTodayWord] = useState(null);
  const [loading, setLoading] = useState(true);
  const [message, setMessage] = useState('');
  const [showGenerateConfirm, setShowGenerateConfirm] = useState(false);
  const [showPublishTomorrowConfirm, setShowPublishTomorrowConfirm] = useState(false);
  const { user } = useAuth();

  useEffect(() => {
    if (user) {
      loadTomorrowWord();
      loadTodayWord();
    }
  }, [user]);

  const shuffleLettersLocal = (word) => {
    if (!word) return '';
    const letters = word.split('');
    for (let i = letters.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [letters[i], letters[j]] = [letters[j], letters[i]];
    }
    return letters.join('');
  };

  const loadTomorrowWord = async () => {
    try {
      const response = await axios.get(`${WORD_API_URL}/admin/tomorrow-word`);
      setTomorrowWord(response.data);
    } catch (error) {
      if (error.response?.status === 403) {
        setMessage('У вас нет прав администратора');
      } else {
        setMessage('Ошибка загрузки слова');
      }
    } finally {
      setLoading(false);
    }
  };

  const loadTodayWord = async () => {
    try {
      const response = await axios.get(`${WORD_API_URL}/admin/today-word`);
      if (response.data && response.data.exists) {
        setTodayWord(response.data);
      } else {
        setTodayWord(null);
      }
    } catch (error) {
      // Today word might not exist, that's okay
      console.log('Today word not loaded:', error);
      setTodayWord(null);
    }
  };

  const handleReshuffleLetters = async () => {
    try {
      // If tomorrow's word is just a preview (not saved in DB yet), shuffle locally
      if (tomorrowWord && !tomorrowWord.exists) {
        const shuffled = shuffleLettersLocal(tomorrowWord.word);
        setTomorrowWord(prev => ({
          ...prev,
          shuffledLetters: shuffled
        }));
        setMessage('Буквы перемешаны (предпросмотр)');
        return;
      }

      const response = await axios.post(`${WORD_API_URL}/admin/reshuffle-tomorrow-letters`);
      setTomorrowWord(response.data);
      setMessage('Буквы перемешаны');
    } catch (error) {
      setMessage(error.response?.data?.error || 'Ошибка перемешивания букв');
    }
  };

  const handleRegenerateTomorrow = async () => {
    try {
      const response = await axios.post(`${WORD_API_URL}/admin/regenerate-tomorrow-word`);
      setTomorrowWord(response.data);
      setMessage('Следующее слово обновлено');
    } catch (error) {
      setMessage(error.response?.data?.error || 'Ошибка обновления слова');
    }
  };

  const handleGenerateTodayWord = async () => {
    try {
      setMessage('Генерация нового слова...');
      const response = await axios.post(`${WORD_API_URL}/admin/generate-word`);
      setTodayWord(response.data);
      setMessage('Новое слово успешно сгенерировано и опубликовано!');
      setShowGenerateConfirm(false);
      // Reload today word to get updated info
      setTimeout(() => loadTodayWord(), 1000);
    } catch (error) {
      setMessage(error.response?.data?.error || 'Ошибка генерации слова');
      setShowGenerateConfirm(false);
    }
  };

  const handlePublishTomorrowAsToday = async () => {
    try {
      setMessage('Публикация текущего слова...');
      const response = await axios.post(`${WORD_API_URL}/admin/publish-tomorrow-word`);
      setTodayWord({
        publishedAt: response.data.publishedAt,
        word: response.data.word,
        shuffledLetters: response.data.shuffledLetters,
        wordId: response.data.wordId,
        exists: true
      });
      setMessage(
        `«${response.data.word}» теперь текущее слово. Предыдущее больше не активно.`
      );
      setShowPublishTomorrowConfirm(false);
      await loadTomorrowWord();
      await loadTodayWord();
    } catch (error) {
      setMessage(error.response?.data?.error || 'Ошибка публикации слова');
      setShowPublishTomorrowConfirm(false);
    }
  };


  if (loading) {
    return (
      <AdminShell section="word">
        <div className="loading">Загрузка...</div>
      </AdminShell>
    );
  }

  return (
    <AdminShell section="word">
      <div className="admin-panel">
        {message && (
          <div className={`alert ${message.includes('Ошибка') ? 'alert-error' : 'alert-success'}`}>
            {message}
          </div>
        )}

        <div className="admin-section panel">
          <h2 className="section-title">Следующее слово</h2>
          {tomorrowWord ? (
            <div className="word-card">
              <p className="meta-line">
                Выйдет при следующей публикации
              </p>
              <LetterGrid letters={tomorrowWord.shuffledLetters || ''} size="md" />
              <div className="answer-pill">{tomorrowWord.word}</div>
              <div className="btn-row">
                <button type="button" onClick={handleReshuffleLetters} className="btn btn-secondary">
                  Перемешать буквы
                </button>
                <button type="button" onClick={handleRegenerateTomorrow} className="btn btn-primary">
                  Новое слово
                </button>
                {IS_LOCAL_DEV && (
                  <button
                    type="button"
                    onClick={() => setShowPublishTomorrowConfirm(true)}
                    className="btn btn-success"
                  >
                    Сделать текущим сейчас
                  </button>
                )}
              </div>

              {IS_LOCAL_DEV && showPublishTomorrowConfirm && (
                <div className="alert alert-warn confirm-dialog">
                  <p>
                    <strong>Опубликовать «{tomorrowWord.word}» как текущее слово сейчас?</strong>
                  </p>
                  <p>
                    Текущее слово перестанет быть активным. Следующее превью
                    подготовится автоматически.
                  </p>
                  <div className="btn-row">
                    <button
                      type="button"
                      onClick={handlePublishTomorrowAsToday}
                      className="btn btn-success"
                    >
                      Да, опубликовать
                    </button>
                    <button
                      type="button"
                      onClick={() => setShowPublishTomorrowConfirm(false)}
                      className="btn btn-secondary"
                    >
                      Отмена
                    </button>
                  </div>
                </div>
              )}
            </div>
          ) : (
            <p className="meta-line">Не удалось загрузить слово</p>
          )}
        </div>

        <div className="admin-section panel">
          <h2 className="section-title">Текущее слово</h2>
          {todayWord ? (
            <div className="word-card">
              <p className="meta-line">
                Опубликовано: {todayWord.publishedAt || todayWord.date || '—'}
              </p>
              <LetterGrid letters={todayWord.shuffledLetters || ''} size="md" />
              <div className="answer-pill">{todayWord.word}</div>
              {IS_LOCAL_DEV && (
                <div className="btn-row">
                  <button
                    type="button"
                    onClick={() => setShowGenerateConfirm(true)}
                    className="btn btn-success"
                  >
                    Заменить слово сейчас
                  </button>
                </div>
              )}

              {IS_LOCAL_DEV && showGenerateConfirm && (
                <div className="alert alert-warn confirm-dialog">
                  <p><strong>Заменить текущее слово?</strong></p>
                  <p>
                    «{todayWord.word}» будет заменено сразу для всех игроков.
                    Это нельзя отменить.
                  </p>
                  <div className="btn-row">
                    <button type="button" onClick={handleGenerateTodayWord} className="btn btn-success">
                      Да, заменить
                    </button>
                    <button
                      type="button"
                      onClick={() => setShowGenerateConfirm(false)}
                      className="btn btn-secondary"
                    >
                      Отмена
                    </button>
                  </div>
                </div>
              )}
            </div>
          ) : (
            <div className="word-card">
              <p className="meta-line">Текущее слово не установлено.</p>
              <button type="button" onClick={handleGenerateTodayWord} className="btn btn-success">
                Сгенерировать слово
              </button>
            </div>
          )}
        </div>

        <ScheduleSettings
          scheduleUrl={`${WORD_API_URL}/admin/schedule`}
          onSaved={async (text) => {
            setMessage(text);
            await loadTomorrowWord();
          }}
          onError={setMessage}
        />
      </div>
    </AdminShell>
  );
}

export default AdminPanel;

